import RoleCard from './RoleCard.svelte';
import ItemCard from './ItemCard.svelte';
import AuraCard from './AuraCard.svelte';
import { awarenessKeyOf } from './auraAwareness.js';

// A collection describes one catalogue (roles, items, auras) to CatalogueBrowser:
// how to search it, how it can be grouped, how it can be sorted, and which facet
// filters it offers. The browser itself holds no knowledge of any collection.

function byDisplayName(left, right) {
    return left.displayName.localeCompare(right.displayName, undefined, { sensitivity: 'base' });
}

function descending(compare) {
    return (left, right) => compare(right, left);
}

function numeric(field) {
    return (left, right) => {
        const leftValue = left[field];
        const rightValue = right[field];
        const leftMissing = leftValue === null || leftValue === undefined;
        const rightMissing = rightValue === null || rightValue === undefined;
        if (leftMissing && rightMissing) return byDisplayName(left, right);
        if (leftMissing) return 1;
        if (rightMissing) return -1;
        if (leftValue === rightValue) return byDisplayName(left, right);
        return rightValue - leftValue;
    };
}

const nameSorts = [
    { value: 'name', label: 'Name (A to Z)', compare: byDisplayName },
    { value: 'name-desc', label: 'Name (Z to A)', compare: descending(byDisplayName) },
];

const noGrouping = { value: 'none', label: 'No grouping', keysOf: () => [], labelOf: () => '' };

const tagGrouping = {
    value: 'tag',
    label: 'Tag',
    keysOf: (entry) => entry.tags || [],
    labelOf: (entry, key) => key,
    emptyLabel: 'No tags',
};

function tagFacet() {
    return {
        id: 'tag',
        label: 'Tag',
        allLabel: 'Any tag',
        options: (entries) => {
            const tags = new Set();
            for (const entry of entries) {
                for (const tag of entry.tags || []) tags.add(tag);
            }
            return [...tags].sort().map((tag) => ({ value: tag, label: tag }));
        },
        matches: (entry, value) => (entry.tags || []).includes(value),
    };
}

function distinctOptions(entries, selector) {
    const values = new Set();
    for (const entry of entries) {
        const value = selector(entry);
        if (value) values.add(value);
    }
    return [...values].sort().map((value) => ({ value, label: value }));
}

export function rolesCollection(factions) {
    const factionOrder = factions.map((faction) => faction.factionSlug);

    return {
        name: 'roles',
        entryNoun: 'role',
        entryNounPlural: 'roles',
        searchPlaceholder: 'Search roles by name, ability, faction or tag',
        searchLabel: 'Search roles',
        card: RoleCard,
        // Grouping by faction puts the card's own accent colour on the section heading.
        accentGrouping: 'faction',
        accentPrefix: 'faction-',
        searchText: (role) =>
            `${role.displayName} ${role.faction} ${role.snippet || ''} ${(role.tags || []).join(' ')} ${(role.categories || []).join(' ')}`,
        groupings: [
            {
                value: 'faction',
                label: 'Faction',
                keysOf: (role) => [role.factionSlug],
                labelOf: (role) => role.faction,
                order: factionOrder,
            },
            tagGrouping,
            {
                value: 'category',
                label: 'Play style',
                keysOf: (role) => role.categories || [],
                labelOf: (role, key) => key,
                order: ['Offence', 'Defence', 'Support', 'Intel'],
                emptyLabel: 'No play style recorded',
            },
            {
                value: 'alignment',
                label: 'A Seer sees',
                keysOf: (role) => (role.viewedAlignment ? [role.viewedAlignment] : []),
                labelOf: (role) => role.viewedAlignment,
                emptyLabel: 'Not recorded',
            },
            noGrouping,
        ],
        sorts: [...nameSorts],
        facets: [
            {
                id: 'faction',
                label: 'Faction',
                allLabel: 'Any faction',
                options: (roles) =>
                    distinctOptions(roles, (role) => role.factionSlug).map((option) => ({
                        value: option.value,
                        label:
                            (roles.find((role) => role.factionSlug === option.value) || {})
                                .faction || option.value,
                    })),
                matches: (role, value) => role.factionSlug === value,
            },
            {
                id: 'category',
                label: 'Play style',
                allLabel: 'Any play style',
                options: (roles) => {
                    const found = new Set();
                    for (const role of roles) {
                        for (const category of role.categories || []) found.add(category);
                    }
                    return [...found]
                        .sort()
                        .map((category) => ({ value: category, label: category }));
                },
                matches: (role, value) => (role.categories || []).includes(value),
            },
            {
                id: 'alignment',
                label: 'A Seer sees',
                allLabel: 'Any alignment',
                options: (roles) => distinctOptions(roles, (role) => role.viewedAlignment),
                matches: (role, value) => role.viewedAlignment === value,
            },
            {
                id: 'killing',
                label: 'Killing',
                allLabel: 'Any killing status',
                options: () => [
                    { value: 'killer', label: 'Seen as a killer' },
                    { value: 'peaceful', label: 'Not a killer' },
                ],
                matches: (role, value) =>
                    value === 'killer' ? !!role.viewedAsKilling : !role.viewedAsKilling,
            },
            {
                id: 'witchcraft',
                label: 'Witchcraft',
                allLabel: 'Any witchcraft status',
                options: () => [
                    { value: 'witch', label: 'Uses witchcraft' },
                    { value: 'mundane', label: 'No witchcraft' },
                ],
                matches: (role, value) =>
                    value === 'witch' ? !!role.usesWitchcraft : !role.usesWitchcraft,
            },
            tagFacet(),
        ],
    };
}

export function itemsCollection() {
    return {
        name: 'items',
        entryNoun: 'item',
        entryNounPlural: 'items',
        searchPlaceholder: 'Search items by name, effect, kind or tag',
        searchLabel: 'Search items',
        card: ItemCard,
        // Grouping by kind puts the card's own accent colour on the section heading.
        accentGrouping: 'kind',
        accentPrefix: 'kind-',
        searchText: (item) =>
            `${item.displayName} ${item.kind || ''} ${item.description || ''} ${(item.tags || []).join(' ')}`,
        groupings: [
            {
                value: 'kind',
                label: 'Kind',
                keysOf: (item) => (item.kindSlug ? [item.kindSlug] : []),
                labelOf: (item) => item.kind,
                emptyLabel: 'Other',
            },
            {
                value: 'type',
                label: 'Active or passive',
                keysOf: (item) => [item.isActive ? 'active' : 'passive'],
                labelOf: (item) => (item.isActive ? 'Active' : 'Passive'),
                order: ['active', 'passive'],
            },
            tagGrouping,
            noGrouping,
        ],
        sorts: [...nameSorts],
        facets: [
            {
                id: 'kind',
                label: 'Kind',
                allLabel: 'Any kind',
                options: (items) => distinctOptions(items, (item) => item.kind),
                matches: (item, value) => item.kind === value,
            },
            {
                id: 'type',
                label: 'Type',
                allLabel: 'Any item type',
                options: () => [
                    { value: 'active', label: 'Active' },
                    { value: 'passive', label: 'Passive' },
                ],
                matches: (item, value) => (value === 'active' ? !!item.isActive : !item.isActive),
            },
            {
                id: 'consumable',
                label: 'Consumable',
                allLabel: 'Any consumable status',
                options: () => [
                    { value: 'yes', label: 'Consumable' },
                    { value: 'no', label: 'Permanent' },
                ],
                matches: (item, value) =>
                    value === 'yes' ? !!item.isConsumable : !item.isConsumable,
            },
            {
                id: 'passable',
                label: 'Can be passed',
                allLabel: 'Any passing status',
                options: () => [
                    { value: 'yes', label: 'Can be passed' },
                    { value: 'no', label: 'Cannot be passed' },
                ],
                matches: (item, value) =>
                    value === 'yes' ? !!item.canBePassed : !item.canBePassed,
            },
            {
                id: 'destructible',
                label: 'Can be destroyed',
                allLabel: 'Any durability',
                options: () => [
                    { value: 'yes', label: 'Can be destroyed' },
                    { value: 'no', label: 'Indestructible' },
                ],
                matches: (item, value) =>
                    value === 'yes' ? !!item.canBeDestroyed : !item.canBeDestroyed,
            },
            {
                id: 'visits',
                label: 'Visits',
                allLabel: 'Any visit status',
                options: () => [
                    { value: 'yes', label: 'Visits are seen' },
                    { value: 'no', label: 'Visits are hidden' },
                ],
                matches: (item, value) =>
                    value === 'yes' ? !!item.areVisitsVisible : !item.areVisitsVisible,
            },
            tagFacet(),
        ],
    };
}

export function aurasCollection() {
    return {
        name: 'auras',
        entryNoun: 'aura',
        entryNounPlural: 'auras',
        searchPlaceholder: 'Search auras by name, effect, source or tag',
        searchLabel: 'Search auras',
        card: AuraCard,
        searchText: (aura) =>
            `${aura.displayName} ${aura.reportedName || ''} ${(aura.variants || []).map((variant) => variant.displayName).join(' ')} ${(aura.sourceCategories || []).join(' ')} ${(aura.effectGroups || []).map((group) => group.label).join(' ')} ${aura.description || ''} ${(aura.tags || []).join(' ')}`,
        groupings: [noGrouping],
        sorts: [
            ...nameSorts,
            { value: 'priority', label: 'Priority (high to low)', compare: numeric('priority') },
        ],
        facets: [
            {
                id: 'cleansable',
                label: 'Cleansable',
                allLabel: 'Any cleansing status',
                options: () => [
                    { value: 'yes', label: 'Cleansable' },
                    { value: 'no', label: 'Not cleansable' },
                ],
                matches: (aura, value) =>
                    value === 'yes' ? !!aura.isCleansable : !aura.isCleansable,
            },
            {
                id: 'known',
                label: 'Bearer awareness',
                allLabel: 'Any bearer status',
                options: () => [
                    { value: 'known', label: 'Known to the bearer' },
                    { value: 'told-when-applied', label: 'Told when applied' },
                    { value: 'hidden', label: 'Hidden from the bearer' },
                ],
                matches: (aura, value) => awarenessKeyOf(aura) === value,
            },
            {
                id: 'source',
                label: 'Source',
                allLabel: 'Any source',
                options: (auras) => {
                    const categories = new Set();
                    for (const aura of auras) {
                        for (const category of aura.sourceCategories || [])
                            categories.add(category);
                    }
                    return [...categories]
                        .sort()
                        .map((category) => ({ value: category, label: category }));
                },
                matches: (aura, value) => (aura.sourceCategories || []).includes(value),
            },
            {
                id: 'effect',
                label: 'Effect',
                allLabel: 'Any effect',
                options: (auras) => {
                    const labels = new Set();
                    for (const aura of auras) {
                        for (const group of aura.effectGroups || []) labels.add(group.label);
                    }
                    return [...labels].sort().map((label) => ({ value: label, label }));
                },
                matches: (aura, value) =>
                    (aura.effectGroups || []).some((group) => group.label === value),
            },
            {
                id: 'silent',
                label: 'Forms',
                allLabel: 'Any number of forms',
                options: () => [
                    { value: 'yes', label: 'Has a silent variant' },
                    { value: 'no', label: 'Single form' },
                ],
                matches: (aura, value) =>
                    value === 'yes' ? !!aura.hasSilentVariant : !aura.hasSilentVariant,
            },
            tagFacet(),
        ],
    };
}

export function collectionFor(name, data) {
    if (name === 'items') return itemsCollection();
    if (name === 'auras') return aurasCollection();
    return rolesCollection(data.factions || []);
}
